import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

const abbr = (n) => {
  if (n == null || isNaN(n)) return "0";
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${+(n / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `${+(n / 1_000).toFixed(1)}k`;
  return String(n);
};

// Explicit "nice" ticks for Truckload Coverage axes (recharts drops auto ticks on flat/decimal domains).
const covAxis = (maxVal) => {
  const m = maxVal > 0 ? maxVal : 10;
  const raw = m / 4;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const f = [1, 1.5, 2, 2.5, 3, 4, 5, 10].find((x) => raw / pow <= x) || 10;
  const step = f * pow;
  return [0, 1, 2, 3, 4].map((i) => Math.round(i * step * 1000) / 1000);
};

const X_TICK = { fontSize: 11, fill: "#475569" };
const Y_TICK = { fontSize: 12, fill: "#475569" };
const CHART_CURSOR = { fill: "rgba(0,47,167,0.06)" };
const CHART_MARGIN = { top: 8, right: 12, left: 12, bottom: 8 };
const BAR_RADIUS = [2, 2, 0, 0];

const StockTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs shadow-md" data-testid="stock-tooltip">
      <p className="mb-1 font-semibold text-slate-800">{label}</p>
      <p className="text-slate-600">Total in Stock: <span className="font-semibold">{Number(d.qty).toLocaleString()}</span></p>
      <p className="text-amber-600">Reserved: <span className="font-semibold">{Number(d.reserved).toLocaleString()}</span></p>
      <p className="text-emerald-700">Available: <span className="font-semibold">{Number(d.covMode ? d.realAvail : d.avail).toLocaleString()}</span></p>
      {d.covMode && (d.covRows?.length ? (
        <>
          {d.covAvg ? (
            <div className="mt-1 border-t border-slate-100 pt-1">
              {d.covRows.map((r) => (
                <p key={r.label} className="text-slate-600">{r.label}: <span className="font-semibold">{Number(r.cov).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></p>
              ))}
              <p className="text-[#002FA7]">Average Coverage ({d.covRows.length} config{d.covRows.length > 1 ? "s" : ""}): <span className="font-semibold">{Number(d.cov).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></p>
            </div>
          ) : (
            <>
              <p className="mt-1 border-t border-slate-100 pt-1 text-slate-600">Required per Truckload: <span className="font-semibold">{Number(d.reqPerTruck).toLocaleString()}</span></p>
              <p className="text-[#002FA7]">Truckloads Supported: <span className="font-semibold">{Number(d.cov).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></p>
            </>
          )}
          {d.covShared && <p className="text-slate-400">Shared across all designs of this label size</p>}
        </>
      ) : (
        <p className="mt-1 border-t border-slate-100 pt-1 text-slate-400">{/additive/i.test(d.compName || "") ? "N/A — additive dosing is design-specific" : d.covScope === "specific" ? "N/A — not used by the selected Bottle Size + Case Pack" : d.covScope === "product" ? "N/A — not used by the selected Bottle Size" : "N/A — not used in any Bottle Size + Case Pack configuration"}</p>
      ))}
    </div>
  );
};

export function VariantChart({ data, title, peak, colored, testId }) {
  const dec = data.some((d) => d.covMode);
  const ticks = dec ? covAxis(peak) : null;
  return (
    <div className="border border-slate-200 p-4 sm:p-6" data-testid={testId}>
      <div className="mb-4 flex items-baseline justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#002FA7]">{title}</p>
        <p className="flex items-center gap-3 text-xs text-slate-400">
          {!dec && <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-[2px] bg-[#f59e0b]" /> Reserved</span>}
          <span>Peak {peak.toLocaleString()}</span>
        </p>
      </div>
      {data.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-sm text-slate-400">No variants to show.</div>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={X_TICK} interval={0} angle={-30} textAnchor="end" height={96} tickMargin={8} />
            <YAxis type="number" allowDataOverflow tickFormatter={dec ? undefined : abbr} width={56} tick={Y_TICK} allowDecimals={dec} interval={0} domain={[0, ticks ? ticks[4] : peak <= 0 ? 10 : Math.ceil(peak * 1.05 * 100) / 100]} ticks={ticks || undefined} />
            <Tooltip cursor={CHART_CURSOR} content={<StockTooltip />} />
            <Bar dataKey="avail" stackId="s" maxBarSize={64} isAnimationActive={false}>
              {data.map((d) => (
                <Cell key={d.typeId} fill={colored ? d.color : "#002FA7"} />
              ))}
            </Bar>
            <Bar dataKey="reserved" stackId="s" fill="#f59e0b" radius={BAR_RADIUS} maxBarSize={64} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function GroupedVariantChart({ groups, yMax, title, peak, testId, actions = null }) {
  const dec = groups.some((g) => g.variants.some((v) => v.covMode));
  const ticks = dec ? covAxis(yMax) : null;
  const top = ticks ? ticks[4] : yMax;
  return (
    <div className="border border-slate-200 p-4 sm:p-6" data-testid={testId}>
      <div className="mb-4 flex items-baseline justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#002FA7]">{title}</p>
        <div className="flex items-center gap-3">
          {actions}
          <p className="flex items-center gap-3 text-xs text-slate-400">
          {!dec && <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-[2px] bg-[#f59e0b]" /> Reserved</span>}
          <span>Peak {peak.toLocaleString()}</span>
          </p>
        </div>
      </div>
      {groups.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-sm text-slate-400">No variants to show.</div>
      ) : (
        <div className="flex items-stretch gap-3 overflow-x-auto">
          {groups.map((g, gi) => (
            <div key={g.comp.id} className="flex min-w-0 flex-col" style={{ flex: `${g.variants.length} 1 0%`, minWidth: 80 + g.variants.length * 48 }} data-testid={`inventory-group-panel-${g.comp.id}`}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={g.variants} margin={{ top: 8, right: 6, left: gi === 0 ? 0 : 6, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={X_TICK} interval={0} angle={-30} textAnchor="end" height={104} tickMargin={6} />
                  <YAxis type="number" allowDataOverflow hide={gi !== 0} width={gi === 0 ? 56 : 0} domain={[0, top]} tickFormatter={dec ? undefined : abbr} tick={Y_TICK} allowDecimals={dec} interval={0} ticks={ticks || undefined} />
                  <Tooltip cursor={CHART_CURSOR} content={<StockTooltip />} />
                  <Bar dataKey="avail" stackId="s" fill={g.comp.color} maxBarSize={64} isAnimationActive={false} />
                  <Bar dataKey="reserved" stackId="s" fill="#f59e0b" radius={BAR_RADIUS} maxBarSize={64} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-2 border-t-2 pt-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-700" style={{ borderColor: g.comp.color }}>{g.comp.name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
